/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type AxiosInstance, type AxiosResponse } from 'axios';
import { createQuery, type CustomUseQueryOptions } from '@/utils/createReactQuery';
import { requestBaseURL } from '@/config/serviceAPI';
import type { BuyAgainPurchasesResponse } from '../interface';

const URL = `${requestBaseURL}/ecomm/b2c/v1/currentUser/purchaseSummary`;

type BuyAgainPurchasesOptions = {
  fromDate?: string;
  toDate?: string;
  partTerminologyId?: string;
  partGroupId?: string;
};

type BuyAgainPurchasesData = BuyAgainPurchasesResponse;

const getBuyAgainPurchases = async (
  options: BuyAgainPurchasesOptions,
  axiosInstance?: AxiosInstance,
) => {
  const { fromDate, toDate, partTerminologyId, partGroupId } = options;

  const params = {
    fromDate: fromDate ?? undefined,
    toDate: toDate ?? undefined,
    partTerminologyId: partTerminologyId ?? undefined,
    partGroupId: partGroupId ?? undefined,
  };

  const response = await getAxios(axiosInstance).get<BuyAgainPurchasesResponse>(URL, {
    params,
  });

  return selector(response);
};

export const selector = (
  response: AxiosResponse<BuyAgainPurchasesResponse>,
): BuyAgainPurchasesData => response.data;

const {
  useData: useBuyAgainPurchasesData,
  prefetch: prefetchBuyAgainPurchases,
  query: buyAgainPurchasesQuery,
} = createQuery<BuyAgainPurchasesData, BuyAgainPurchasesOptions>('buyAgain', getBuyAgainPurchases);

type BuyAgainPurchasesQueryOptions<SelectData> = BuyAgainPurchasesOptions &
  CustomUseQueryOptions<BuyAgainPurchasesData, SelectData> & { enabled?: boolean };

const useBuyAgainPurchases = <SelectData = BuyAgainPurchasesData>({
  ...options
}: BuyAgainPurchasesQueryOptions<SelectData>) => {
  const { enabled = true } = options;

  return useBuyAgainPurchasesData<SelectData>({
    ...options,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled,
  });
};

export { useBuyAgainPurchases, prefetchBuyAgainPurchases, buyAgainPurchasesQuery };

export type { BuyAgainPurchasesData };
